import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { userPreferencesService } from '../services/userPreferencesService';

interface CustomizationSettings {
  fontSize: 'small' | 'medium' | 'large';
  fontFamily: 'default' | 'serif' | 'monospace';
  compactMode: boolean;
  density: 'comfortable' | 'middle' | 'compact';
  primaryColor?: string;
  layoutWidth?: 'fluid' | 'container';
}

/**
 * Debounce helper for API calls
 */
let themeSyncTimeout: ReturnType<typeof setTimeout> | null = null;

const debouncedSyncThemeToAPI = (themeSettings: any) => {
  // Clear existing timeout
  if (themeSyncTimeout) {
    clearTimeout(themeSyncTimeout);
  }

  // Set new timeout to sync after 1 second of inactivity
  themeSyncTimeout = setTimeout(async () => {
    try {
      await userPreferencesService.updateThemeSettings(themeSettings);
    } catch (error) {
      console.warn('Failed to sync theme settings to API:', error);
      // Fail silently - localStorage will persist the changes
    }
  }, 1000);
};

interface GlobalState {
  sidebarCollapsed: boolean;
  sidebarWidth: number;
  layoutType: 'vertical' | 'horizontal' | 'mini';
  theme: 'light' | 'dark';
  customization: CustomizationSettings;
  breadcrumbs: Array<{ title: string; path?: string }>;
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    timestamp: Date;
  }>;

  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarWidth: (width: number) => void;
  setLayoutType: (layoutType: 'vertical' | 'horizontal' | 'mini') => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setCustomization: (customization: Partial<CustomizationSettings>) => void;
  setBreadcrumbs: (breadcrumbs: Array<{ title: string; path?: string }>) => void;
  addNotification: (
    notification: Omit<GlobalState['notifications'][0], 'id' | 'timestamp'>,
  ) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  loadFromAPI: () => Promise<void>;
}

export const useGlobalStore = create<GlobalState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarWidth: 247,
      layoutType: 'vertical',
      theme: 'light',
      customization: {
        fontSize: 'medium',
        fontFamily: 'default',
        compactMode: false,
        density: 'middle',
        primaryColor: 'blue',
        layoutWidth: 'container',
      },
      breadcrumbs: [],
      notifications: [],

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
        // Sync to API
        debouncedSyncThemeToAPI({ sidebarCollapsed: collapsed });
      },

      setSidebarWidth: (width: number) => {
        set({ sidebarWidth: width });
        // Sync to API
        debouncedSyncThemeToAPI({ sidebarWidth: width });
      },

      setLayoutType: (layoutType: 'vertical' | 'horizontal' | 'mini') => {
        const sidebarCollapsed = layoutType === 'mini' || layoutType === 'horizontal';
        set({
          layoutType,
          sidebarCollapsed,
        });
        // Sync to API
        debouncedSyncThemeToAPI({ layoutType, sidebarCollapsed });
      },

      setTheme: (theme: 'light' | 'dark') => {
        set({ theme });
        // Sync to API
        debouncedSyncThemeToAPI({ theme });
      },

      setCustomization: (customization: Partial<CustomizationSettings>) => {
        set((state: GlobalState) => ({
          customization: { ...state.customization, ...customization },
        }));
        // Sync to API - map customization to theme settings format
        debouncedSyncThemeToAPI({
          componentSize: customization.fontSize,
          fontFamily: customization.fontFamily,
          layoutDensity: customization.density,
          layoutWidth: customization.layoutWidth,
        });
      },

      setBreadcrumbs: (breadcrumbs: Array<{ title: string; path?: string }>) =>
        set({ breadcrumbs }),

      addNotification: (notification: Omit<GlobalState['notifications'][0], 'id' | 'timestamp'>) =>
        set((state: GlobalState) => ({
          notifications: [
            ...state.notifications,
            {
              ...notification,
              id: Date.now().toString(),
              timestamp: new Date(),
            },
          ],
        })),

      removeNotification: (id: string) =>
        set((state: GlobalState) => ({
          notifications: state.notifications.filter((notif) => notif.id !== id),
        })),

      clearNotifications: () => set({ notifications: [] }),

      /**
       * Load theme settings from API and merge with local storage
       */
      loadFromAPI: async () => {
        try {
          const preferences = await userPreferencesService.getUserPreferences();
          if (preferences.themeSettings) {
            const theme = preferences.themeSettings;
            set((state) => ({
              theme: theme.theme || state.theme,
              layoutType: theme.layoutType || state.layoutType,
              sidebarCollapsed: theme.sidebarCollapsed ?? state.sidebarCollapsed,
              sidebarWidth: theme.sidebarWidth || state.sidebarWidth,
              customization: {
                ...state.customization,
                fontSize: (theme.componentSize as any) || state.customization.fontSize,
                fontFamily: (theme.fontFamily as any) || state.customization.fontFamily,
                density: (theme.layoutDensity as any) || state.customization.density,
                layoutWidth:
                  (theme.layoutWidth as 'fluid' | 'container') ??
                  state.customization.layoutWidth ??
                  'container',
              },
            }));
          }
        } catch (error) {
          console.warn('Failed to load theme settings from API:', error);
          // Keep using localStorage settings
        }
      },
    }),
    {
      name: 'global-settings',
      partialize: (state) => ({
        theme: state.theme,
        customization: state.customization,
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarWidth: state.sidebarWidth,
        layoutType: state.layoutType,
      }),
    },
  ),
);
